import { TransformedDeliveryService } from "../../utils/serviceMapper";
import { useServicesAndWarrantiesContext } from "../../ServicesAndWarrantiesContext";
import { useEffect } from "react";
import styles from "./DeliveryServices.module.scss";
import OptionList from "@JohnLewisPartnership/jl-design-system/dist/components/option/OptionList";
import { DeliveryServiceItem } from "./DeliveryServiceItem";
import {
  SERVICE_QUESTIONNAIRE_ESI_COMPLETE,
  SERVICE_QUESTIONNAIRE_ESI_SERVICE_SELECTED,
} from "../../../../constants/constants";
import ESIInclude from "@JohnLewisPartnership/jl-esi-components";

type Props = {
  services: TransformedDeliveryService[];
  availableToOrder: boolean;
  deliveryServiceQuestionnaireEnabled: boolean;
};

type ServiceQuestionnaireResult = {
  serviceId: string;
  success: boolean;
};

type ServiceQuestionnaireRequest = {
  dmsId: string;
  serviceId: string;
  parentItemId: string;
};

type GasCheckServiceQuestionnaireRequest = {
  services: [
    {
      dmsId: string;
      serviceId: string;
    },
    {
      dmsId: string;
      serviceId: string;
    },
  ];
  parentItemId: string;
};

export function DeliveryServices({
  availableToOrder,
  services,
  deliveryServiceQuestionnaireEnabled,
}: Props) {
  const { resetSelectedServices, toggleSelectedService } =
    useServicesAndWarrantiesContext();

  const handleDeliveryServiceQuestionnaireCompletion = (
    event: CustomEvent<ServiceQuestionnaireResult>,
  ) => {
    const { serviceId, success } = event.detail;
    if (success) {
      toggleSelectedService(serviceId);
    }
  };

  const onToggle = (service: TransformedDeliveryService) => {
    const { id, skuId, selected } = service;
    if (deliveryServiceQuestionnaireEnabled && !selected) {
      openServiceQuestionnaire(skuId, id);
    } else {
      toggleSelectedService(id);
    }
  };

  useEffect(() => {
    window.addEventListener("beforeunload", resetSelectedServices);

    if (deliveryServiceQuestionnaireEnabled) {
      window.addEventListener(
        SERVICE_QUESTIONNAIRE_ESI_COMPLETE,
        handleDeliveryServiceQuestionnaireCompletion,
      );
    }

    return () => {
      window.removeEventListener("beforeunload", resetSelectedServices);
      window.removeEventListener(
        SERVICE_QUESTIONNAIRE_ESI_COMPLETE,
        handleDeliveryServiceQuestionnaireCompletion,
      );
    };
  }, [resetSelectedServices, handleDeliveryServiceQuestionnaireCompletion]);

  if (!services.length) return null;

  return (
    <div data-testid="product:delivery-services" className={styles.services}>
      <h2 className={styles.title}>Installation & Disposal</h2>
      <OptionList isButtonList={false} isRow={false}>
        {services.map((service, index) => {
          return (
            <DeliveryServiceItem
              checked={service.selected}
              title={service.title}
              key={`service-${index}`}
              serviceId={service.id}
              description={service.description}
              display={service.display}
              value={service.value}
              toggleCheck={() => onToggle(service)}
              disabled={!availableToOrder}
            />
          );
        })}
      </OptionList>
      {deliveryServiceQuestionnaireEnabled && (
        <div data-testid={"service-questionnaire-esi-wrapper"}>
          <ESIInclude
            src={"/service-questionnaire/index.html"}
            id={"service-questionnaire"}
          />
        </div>
      )}
    </div>
  );
}

function openServiceQuestionnaire(skuId: string, serviceId: string) {
  const dmsId = removeDmsPrefix(skuId);
  const checkForGas = ["3913", "6901", "3905", "8301", "3911", "3908"];

  let payload;
  if (checkForGas.includes(dmsId)) {
    payload = buildGasCheckPayload(dmsId, serviceId);
  } else {
    payload = buildPayload(dmsId, serviceId);
  }

  window.dispatchEvent(
    new CustomEvent(SERVICE_QUESTIONNAIRE_ESI_SERVICE_SELECTED, {
      detail: payload,
    }),
  );
}

function buildPayload(dmsId: string, serviceId: string) {
  const nonGasPayload: ServiceQuestionnaireRequest = {
    dmsId: dmsId,
    serviceId: serviceId,
    parentItemId: "not-required-for-pdp",
  };
  return nonGasPayload;
}

function buildGasCheckPayload(dmsId: string, serviceId: string) {
  const gasPayload: GasCheckServiceQuestionnaireRequest = {
    services: [
      {
        dmsId: dmsId,
        serviceId: serviceId,
      },
      {
        dmsId: dmsId,
        serviceId: serviceId,
      },
    ],
    parentItemId: "not-required-for-pdp",
  };
  return gasPayload;
}

export function removeDmsPrefix(inputString: string) {
  if (inputString.startsWith("DMS")) {
    return inputString.slice(3);
  } else {
    return inputString;
  }
}
